import { buildWhereFromFilters } from '../sql/buildWhereFromFilters';
import { buildFBDFilter } from '../sql/buildFBDFilter';
import { toPlaceholdersString } from '../sql/toPlaceholdersString';
import { DatabaseClientInterface } from '../sql/DatabaseClientInterface';
import { DesignEnum } from '../enums/DesignEnum';
import { AttributesFilterInputInterface } from '../interfaces/AttributesFilterInputInterface';
import { LocalesEnum } from '../enums/LocalesEnum';
import { ProductInterface } from '../interfaces/ProductInterface';
import { AttributesClassInterface, isAttributesClassInterface } from '../interfaces/AttributesClassInterface';
import { SolutionProductInterface } from '../interfaces/SolutionProduct';
import { extractJsonFieldSql } from '../sql/extractJsonFieldSql';
import { buildWhereByShoppingCenter } from '../sql/buildWhereByShoppingCenter';

export const uniq = (v: string, i: number, a: string[]) => a.indexOf(v) === i;

export class FindByDesignResolver {
    constructor(
        private readonly db: DatabaseClientInterface,
        private readonly mapToProduct: (row: { id: string; label: string; attributes: string; }) => ProductInterface,
        private readonly mapToValue: (row: any) => string,
        private readonly mapFBD: (row: any) => { template: string[], linkages: string[] }
    ) {
    }

    async ByValveCount(
        design: DesignEnum,
        valveFilter: AttributesFilterInputInterface,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        shoppingCenter: string,
    ): Promise<number> {
        const data = await this.ByValve(design, valveFilter, filter, rawName, shoppingCenter);
        return data.length;
    }

    async ByValve(
        design: DesignEnum,
        valveFilter: AttributesFilterInputInterface,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        shoppingCenter: string,
    ): Promise<SolutionProductInterface[]> {
        const [whereValve, valveFilterParams] = buildWhereFromFilters(valveFilter, [], rawName);
        const [designWhere, designParams] = buildFBDFilter(design, rawName);
        const params = await this.db.getMany(`
            SELECT attributes
            FROM find_by_design
            WHERE ${designWhere} ${whereValve.length > 0 ? ` AND ${whereValve}` : ''}
        `, [...designParams, ...valveFilterParams], this.mapFBD);
        const labels = params
        .reduce((acc, item) => [...acc, ...item.template], [] as string[])
        .filter(uniq);
        const linkageLabels = params
        .reduce((acc, item) => [...acc, ...item.linkages], [] as string[])
        .filter(uniq);

        const [where, filterParams] = buildWhereFromFilters(filter, [], rawName);
        const [shopWhere, shopParams] = buildWhereByShoppingCenter(rawName, shoppingCenter);
        const sql = `SELECT id, label, attributes
                     FROM product
                     WHERE label IN (${toPlaceholdersString(labels)})
                         ${where.length > 0 ? ` AND ${where}` : ''} ${shopWhere.length > 0 ? ` AND ${shopWhere}` : ''}`;

        const products = await this.db.getMany(
            sql,
            [...labels, ...filterParams, ...shopParams],
            this.mapToProduct,
        );
        const accessories = await this.db.getMany(
            `SELECT id, label, attributes
             FROM product
             WHERE label IN (${toPlaceholdersString(linkageLabels)})
                 ${shopWhere.length > 0 ? ` AND ${shopWhere}` : ''}`,
            [...linkageLabels, ...shopParams],
            this.mapToProduct
        );

        const productsMap = new Map<string, AttributesClassInterface>();
        [...products, ...accessories].forEach(
            (item) => productsMap.set(item.label, item.getAttributes()),
        );
        const solutionProductsUniq = new Map<string, string[]>();
        params.forEach(({ template, linkages }) => {
            template.forEach((label) => {
                const current = solutionProductsUniq.get(label);
                if (!current) {
                    solutionProductsUniq.set(label, linkages);
                } else {
                    solutionProductsUniq.set(
                        label,
                        [...current, ...linkages].filter(uniq),
                    );
                }
            });
        });

        return [...solutionProductsUniq.entries()]
        .map(([label, linkages]) => ({
            product: productsMap.get(label),
            accessories: linkages
            .map((accessoryLabel) => productsMap.get(accessoryLabel))
            .filter<AttributesClassInterface>(isAttributesClassInterface),
        }))
        .filter((item): item is SolutionProductInterface => isAttributesClassInterface(item.product))
        .sort((a, b) => {
            // @TODO change to localeCompare ?
            if (a.product.Label < b.product.Label) {
                return -1;
            }
            if (a.product.Label > b.product.Label) {
                return 1;
            }
            return 0;
        });
    }

    async ByValveValues(
        id: string,
        design: DesignEnum,
        rawName: LocalesEnum,
    ): Promise<string[]> {
        const safeId = parseInt(id, 10).toString();
        const [designWhere, designParams] = buildFBDFilter(design, rawName);

        const sql = `
            SELECT attrs.value as value, count(*) as count
            FROM find_by_design
                     CROSS JOIN json_each(${extractJsonFieldSql('attributes', `$.${safeId}.${rawName}`)}) as attrs
            WHERE ${designWhere}
            GROUP BY attrs.value
            ORDER BY attrs.value
        `;
        return this.db.getMany(sql, designParams, this.mapToValue);
    }
}
